import { DateRange } from '@angular/material/datepicker';
import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { TimeResolution } from '@vi-iot/client/index';
import { AppState } from '../app.state';

import {
  ChartSelectedProperties,
  TrendingData,
  TrendingDateseriesError,
  TrendingEnumMappedProperties,
  TrendingEnumSeriesData,
  TrendingItem,
  VisiblePlotBandFeature,
} from './trending.model';
import { TrendingState } from './trending.state';
import { RemoteConfigSelectors } from '../remote-config';
import { AuthSelectors } from '../auth';

export namespace TrendingSelectors {
  export const trendingState = (state: AppState): TrendingState => state.trending;

  export function getState(): MemoizedSelector<
    AppState,
    TrendingState,
    DefaultProjectorFn<TrendingState>
  > {
    return createSelector(trendingState, (state: TrendingState) => state);
  }

  export function getDataSeries(): MemoizedSelector<
    AppState,
    TrendingData,
    DefaultProjectorFn<TrendingData>
  > {
    return createSelector(trendingState, (state: TrendingState) => state.dataSeries);
  }

  export function getDateseriesError(): MemoizedSelector<
    AppState,
    TrendingDateseriesError,
    DefaultProjectorFn<TrendingDateseriesError>
  > {
    return createSelector(trendingState, (state: TrendingState) => state.dateseriesError);
  }

  export function getChartSelectedProperties(
    chartName: string
  ): MemoizedSelector<
    AppState,
    ChartSelectedProperties,
    DefaultProjectorFn<ChartSelectedProperties>
  > {
    return createSelector(trendingState, (state: TrendingState) =>
      state.chartsSelectedProperties?.find((entry) => entry.chartName === chartName)
    );
  }

  export const getEnumPropertiesMapping = createSelector(
    trendingState,
    (state: TrendingState): TrendingEnumMappedProperties => {
      return state.dataSeries?.enum_properties || {};
    }
  );

  export const selectEnumSeries = createSelector(
    trendingState,
    (state: TrendingState): TrendingEnumSeriesData[] => state.dataSeries?.enum_series || []
  );

  export const getDateRange = createSelector(
    trendingState,
    (state: TrendingState): DateRange<Date> => {
      return state.dateRange;
    }
  );

  export const getResoultion = createSelector(
    trendingState,
    (state: TrendingState): TimeResolution => {
      return state.resolution;
    }
  );

  export const getNavigatorMin = createSelector(
    trendingState,
    (state: TrendingState) => state.navigatorMin
  );

  export const getNavigatorMax = createSelector(
    trendingState,
    (state: TrendingState) => state.navigatorMax
  );

  export const selectTrendingTabData = createSelector(
    trendingState,
    (state: TrendingState) => state.trendingTabData
  );

  export const selectPowerTrendingChartsLabel = createSelector(
    trendingState,
    (state: TrendingState): TrendingItem[] =>
      state.energyStorageTrendingLabel.powerTrendingChartsLabel
  );

  export const selectStateOfChargeLabel = createSelector(
    trendingState,
    (state: TrendingState): TrendingItem[] => state.energyStorageTrendingLabel.stateOfChargeLabel
  );

  export const selectBatteryModuleVoltageLabel = createSelector(
    trendingState,
    (state: TrendingState): TrendingItem[] =>
      state.energyStorageTrendingLabel.batteryModuleVoltageLabel
  );

  export const selectTemperaturesLabel = createSelector(
    trendingState,
    (state: TrendingState): TrendingItem[] => state.energyStorageTrendingLabel.temperaturesLabel
  );

  export const selectIsAllowedToShowViUnifyButton = createSelector(
    RemoteConfigSelectors.selectRsaViUnifyAccessIds,
    AuthSelectors.getLoginId(),
    AuthSelectors.isRsaUser(),
    (rsaViUnifyAccessIds: string[], loginId: string, isRsaUser: boolean): boolean => {
      return isRsaUser && rsaViUnifyAccessIds.some((accessId) => accessId === loginId);
    }
  );

  export function selectVisiblePlotBandFeatures(
    chartName: string
  ): MemoizedSelector<
    AppState,
    VisiblePlotBandFeature[],
    DefaultProjectorFn<VisiblePlotBandFeature[]>
  > {
    return createSelector(trendingState, (state: TrendingState): VisiblePlotBandFeature[] =>
      state.visiblePlotBandFeatures.filter((feature) => feature.chartName === chartName)
    );
  }
}
